import { useState } from 'react'
import { errorMessage } from '../lib/api'
import { useCreatePark } from '../lib/hooks'

const SLUG = /^[a-z0-9-]{2,64}$/

/** "Recreatiepark De Hoeve" -> "recreatiepark-de-hoeve" */
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

/** Creates a park so a new campsite does not need a command line. The drawing follows later. */
export default function AddPark({ onCreated }: { onCreated: (slug: string) => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [ownSlug, setOwnSlug] = useState(false)
  const [locale, setLocale] = useState('nl')
  const [currency, setCurrency] = useState('EUR')
  const create = useCreatePark()

  const effectiveSlug = ownSlug ? slug.trim() : slugify(name)
  const slugOk = SLUG.test(effectiveSlug)
  const nameOk = name.trim() !== ''

  if (!open) {
    return (
      <section className="card">
        <div className="card-head" style={{ marginBottom: 0 }}>
          <div>
            <h2>Park toevoegen</h2>
            <p className="muted small">Maakt een leeg park aan; de plattegrond en plaatsen komen daarna.</p>
          </div>
          <div className="spacer" />
          <button type="button" className="btn primary" aria-expanded={false} onClick={() => setOpen(true)}>
            Park toevoegen
          </button>
        </div>
      </section>
    )
  }

  return (
    <form
      className="card"
      onSubmit={(event) => {
        event.preventDefault()
        if (!slugOk || !nameOk) return
        create.mutate(
          { slug: effectiveSlug, name: name.trim(), locale: locale.trim(), currency: currency.trim() },
          {
            onSuccess: (park) => {
              setOpen(false)
              setName('')
              setSlug('')
              setOwnSlug(false)
              onCreated(park?.slug ?? effectiveSlug)
            },
          },
        )
      }}
    >
      <h2>Park toevoegen</h2>
      <div className="grid cols-4">
        <div className="field">
          <label htmlFor="new-park-name">Naam</label>
          <input
            id="new-park-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="new-park-slug">Slug</label>
          <input
            id="new-park-slug"
            type="text"
            value={effectiveSlug}
            onChange={(event) => {
              setOwnSlug(true)
              setSlug(event.target.value)
            }}
          />
          <span className="help">Komt in de URL. Kleine letters, cijfers en streepjes, 2 tot 64 tekens.</span>
        </div>
        <div className="field">
          <label htmlFor="new-park-locale">Taal</label>
          <input id="new-park-locale" type="text" value={locale} onChange={(event) => setLocale(event.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="new-park-currency">Valuta</label>
          <input
            id="new-park-currency"
            type="text"
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
          />
        </div>
      </div>
      <p className="muted small" style={{ margin: '0.6rem 0 0' }}>
        Het park krijgt een voorlopig kaartvlak van 2000 bij 1400 pixels tot de echte tekening er is.
      </p>
      <div className="actions">
        <button type="submit" className="btn primary" disabled={!slugOk || !nameOk || create.isPending}>
          {create.isPending ? 'Aanmaken…' : 'Aanmaken'}
        </button>
        <button type="button" className="btn" onClick={() => setOpen(false)}>
          Annuleren
        </button>
        {!nameOk && <span className="muted small">Vul een naam in.</span>}
        {nameOk && !slugOk && <span className="inline-error">Slug moet 2 tot 64 tekens zijn: a-z, 0-9 en streepjes.</span>}
        {create.isError && <span className="inline-error">{errorMessage(create.error)}</span>}
      </div>
    </form>
  )
}
